import { provide } from "@lit/context";
import {
    type AppLayout,
    CompanyStatus,
    Feature,
    PartnerIntegration,
    changelog,
    fullVersion,
    getUpdateInfo,
} from "@pentacode/core";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import "../components/avatar/avatar";
import "../components/color-scheme/signal-watcher";
import { type EnabledFeatures, enabledFeatures, loadEnabledFeatures } from "../contexts/features-context";
import { app, appMode, router } from "../init";
import { restart } from "../lib/restart";
import { singleton } from "../lib/singleton";
import { showAcceptTermsDialog } from "../lib/util";
import { ErrorHandling } from "../mixins/error-handling";
import { Routing, failed, ready } from "../mixins/routing";
import { ServiceWorker } from "../mixins/service-worker";
import { StateMixin } from "../mixins/state";
import "../routes/dashboard/dashboard";
import "../routes/employees/employees";
import "../routes/exports/exports";
import "../routes/feedback/feedback";
import "../routes/issues/issues";
import "../routes/login/login";
import "../routes/planning/planning";
import "../routes/recruiting/recruiting";
import "../routes/reports/reports";
import "../routes/revenues/revenues";
import "../routes/roster/roster";
import "../routes/select_company/select_company";
import "../routes/settings/settings";
import "../routes/time";
import { config, mixins, shared } from "../styles";
import "../styles/print.css";
import { alert, confirm } from "./alert-dialog";
import { LAYOUT_STORAGE_KEY as ASSISTANT_LAYOUT_STORAGE_KEY, isCssLength } from "./assistant/chat-layout";
import { ensureAssistantLoaded } from "./assistant/load-assistant";
import { openHelp } from "./help";
import "./popover";
import "./set-password";
import "./terms/accept-terms-dialog";
import type { AcceptTermsDialog } from "./terms/accept-terms-dialog";
import "./update-installed-dialog";
import type { UpdateInstalledDialog } from "./update-installed-dialog";
// initial registration of all components (so you won't need to reregister in every file)
import type { Logo } from "@pentacode/ui/components";
import { activeIcon } from "@pentacode/ui/components";
import { type AppPage, appRoute, firstAccessiblePage, hasPageAccess, menuPages } from "../routes/app-routes";
import { routeAccess } from "../routes/route-access";

function isAssistantBubbleInitiallyDocked(): boolean {
    try {
        const raw = JSON.parse(localStorage.getItem(ASSISTANT_LAYOUT_STORAGE_KEY) || "{}");
        if (typeof raw !== "object" || raw === null) return true;
        return !isCssLength(raw.bubbleX) && !isCssLength(raw.bubbleY);
    } catch {
        return true;
    }
}

@customElement("ptc-app")
export class App extends ServiceWorker(ErrorHandling(Routing(StateMixin(LitElement)))) {
    @provide({ context: enabledFeatures })
    @state()
    private _features: EnabledFeatures = loadEnabledFeatures();

    static styles = [
        config,
        shared,
        css`
            :host {
                --centered-left-offset: 0;
                --centered-transform-x: 0;

                --centered-margin-inline: 0;

                --menu-transition: 0.2s ease-in-out;

                display: block;
                ${mixins.fullbleed()};
                display: flex;
                flex-direction: column;
                background: var(--color-bg);
                container: ptc-app / inline-size;
            }

            .offline {
                padding: 0.5em;
                text-align: center;
                background: var(--red);
                color: var(--color-bg);
                letter-spacing: 0.2em;
            }

            .main {
                flex: 1;
                align-self: center;
                box-sizing: border-box;
                /* 100% (not 100vw): the app root owns the viewport standalone,
                   but only a sub-box of the partner page when embedded. */
                width: min(var(--app-layout-width, 100%), 100%);
                height: 100%;
                transition: width var(--menu-transition);
            }

            :host(:not([data-layout="dynamic"])) {
                --app-layout-width: 1600px;
                --centered-left-offset: 50%;
                --centered-transform-x: -50%;
                --centered-margin-inline: auto;
            }

            .main-menu {
                --menu-bg: var(--color-bg);
                padding-bottom: 0.5em;
                background: var(--menu-bg);
                border-right: solid 1px var(--shade-1);
                max-width: 15em;
                box-sizing: border-box;
                transition: background-color var(--menu-transition);
                overflow: auto;
                overflow-x: hidden;
                z-index: 1;
            }

            .main-menu:hover,
            .main-menu[data-bubble-hover] {
                z-index: 4;
            }

            .main-menu:has(ptc-popover.showing) {
                z-index: 4;
            }

            .logo-wrapper {
                padding: 1.5em 0.65em 0 0.95em;
                overflow: visible;
                position: relative;
            }

            .logo-wrapper .logo {
                /* height drives width via the element's per-variant aspect-ratio. */
                height: 2.07em;
                /* The menu has its own width transition; let the logo snap so the two
                   morphs don't double-animate against each other. */
                --logo-transition-duration: 0ms;
            }

            span > ptc-ui-product-logo.icon {
                height: 1em;
                width: 1.5em;
                line-height: 1em;
                filter: grayscale(100%);
                opacity: 0.7;
            }

            .changelog {
                width: 25em;
                font-size: var(--font-size-small);
                text-align: left;
            }

            .changelog > :first-child {
                margin-top: calc(-1 * (var(--font-size-tiny) + 0.3em)); // offset the version and it's top padding
            }

            .changelog-popover {
                padding: 0;
                z-index: 100;
            }

            .changelog-scroller {
                max-height: 35em;
            }

            .changelog-version {
                font-size: var(--font-size-tiny);
                color: var(--shade-4);
                text-align: center;
                padding: 0.3em;
            }

            .main-menu > button,
            .main-menu > a,
            .main-menu > ptc-color-scheme-signal-watcher,
            .main-menu > .appearance-controls {
                font-size: var(--font-size-large);
                font-weight: normal;
                background: transparent;
                text-align: left;
                font-weight: 400;
                padding: 0.45em 0.3em;
                margin: 0.25em 0.5em 0 0.5em;
                border-radius: var(--border-radius);
                display: flex;
                align-items: center;
            }

            .main-menu > ptc-color-scheme-signal-watcher {
                margin: 0 1rem;
                padding: 0;
                font-size: 1rem;
            }

            .main-menu > ptc-color-scheme-signal-watcher > * {
                margin: 0 1rem 0 0;
                flex: none;
            }

            .appearance-controls:hover {
                cursor: pointer;
                background-color: var(--shade-1);
            }

            .appearance-controls-form {
                text-align: start;
                display: flex;
                align-items: start;
                flex-direction: column;
                gap: 1rem;
                padding: 0.5rem;
            }

            .appearance-controls-form label {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }
            .appearance-controls-form legend {
                margin-bottom: 0.5rem;
                font-size: var(--font-size-medium);
            }

            .main-menu > a {
                text-decoration: none;

                &:hover {
                    box-shadow: inset var(--shade-1) 0 0 0 1000px;

                    & > ptc-ui-icon {
                        visibility: visible;
                    }
                }

                & span {
                    display: flex;
                    align-items: center;
                }

                & > ptc-ui-icon {
                    visibility: hidden;
                    height: 1em;
                    line-height: 1em;
                }
            }

            .button-label {
                transition-duration: 0.15s;
                transition-property: transform, opacity;
                padding-left: 0.25em;
                flex: 1;
                overflow: hidden;
                font-size: 0.9em;
                letter-spacing: 0.02em;
                white-space: nowrap;
            }

            .button-label > :not(:last-child) {
                margin-right: 0.5em;
            }

            .main-menu > :is(button, a, .appearance-controls) ptc-ui-icon {
                transition: transform 0.3s;
                flex: none;
            }

            .main-menu > button[active] {
                box-shadow: none;
                color: var(--color-primary);
                background: transparent;
                font-weight: bold;
            }

            .main-views {
                position: relative;
                background: var(--color-bg);
                z-index: 3;
                isolation: isolate;
            }

            .main-views > * {
                ${mixins.fullbleed()};
                overflow: auto;
            }

            .main-menu .account-button {
                padding: calc(0.25em + 0.45em) calc(0.3em + 0.5em) 0.45em;
                margin: 0;

                z-index: 1;
            }
            .account-button ptc-avatar {
                flex: none;
                font-size: var(--font-size-tiny);
            }

            .account-info {
                padding-left: 0.5em;
                flex: 1;
                overflow: hidden;
            }

            .account-email {
                font-weight: 600;
                font-size: var(--font-size-small);
            }

            .account-name {
                font-weight: bold;
                font-size: var(--font-size-small);
            }

            .account-popover button {
                padding: 0.4em;
            }

            .version {
                text-align: center;
                font-size: var(--font-size-tiny);
                opacity: 0.5;
                margin: -1.5em auto 0.2em auto;
            }

            .main-menu button[active] .pill {
                color: var(--color-bg);
            }

            .double-separator {
                border-bottom: solid 1px var(--shade-2);
                border-top: solid 1px var(--shade-2);
                height: 0.5em;
                margin: 0.5em 0.5em 0em 0.5em;
            }

            .assistant-dock {
                position: sticky;
                bottom: 0.5em;
                align-self: flex-start;
                width: 5.5rem;
                height: 5.5rem;
                margin: 0.5em 0.5em 0.5em 1em;
                border-radius: 0.5em;
                border: 2px dashed transparent;
                transition:
                    border-color 0.15s ease-out,
                    width var(--menu-transition),
                    height 0.3s ease-out,
                    margin 0.3s ease-out;
                flex-shrink: 0;
                box-sizing: border-box;
            }

            .assistant-dock[data-undocked]:not([data-drag-active]) {
                height: 0;
                margin: 0;
                border-width: 0;
            }

            .assistant-dock[data-drag-active] {
                border-color: var(--shade-2);
            }

            .assistant-dock[data-drag-hover] {
                border-color: var(--color-primary);
            }

            .beta-tag {
                background: var(--color-highlight);
                background: var(--gradient-purple);
                color: var(--color-bg);
                position: absolute;
                left: 28px;
                bottom: -2px;
                padding: 1px 3px;
                z-index: 1;
                font-size: 9px;
                border: 1px solid var(--menu-bg);
                transition: border-color var(--menu-transition);
            }

            @container ptc-app (width <= 1400px) {
                .main:has(> .main-menu) {
                    padding-left: 4em;
                }

                .main-menu {
                    left: 0;
                    top: 0;
                    bottom: 0;
                    position: absolute;
                    max-width: 4em;
                    transition: var(--menu-transition);
                    transition-property: background-color, max-width;
                }

                .main-menu:hover,
                .main-menu[data-bubble-hover] {
                    z-index: 11;
                    max-width: 20em;
                    box-shadow: rgba(0, 0, 0, 0.1) 1px 0 5px;
                    border-color: transparent;
                    --menu-bg: hsl(0 0 95%);
                    background-color: var(--menu-bg);
                }

                .main-menu:not(:hover):not([data-bubble-hover]) {
                    scrollbar-width: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover])::-webkit-scrollbar {
                    display: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover]) {
                    scrollbar-width: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover])::-webkit-scrollbar {
                    display: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover]) {
                    scrollbar-width: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover])::-webkit-scrollbar {
                    display: none;
                }

                .main-menu:not(:hover):not([data-bubble-hover]) .assistant-dock {
                    width: 3rem;
                    height: 3rem;
                    margin: 4px;
                }

                .main-menu:not(:hover):not([data-bubble-hover]) .assistant-dock[data-undocked]:not([data-drag-active]) {
                    height: 0;
                }

                .main-menu:not(:hover):not([data-bubble-hover]) .button-label,
                .main-menu:not(:hover):not([data-bubble-hover]) .account-info {
                    transform: translateX(0.5rem);
                    opacity: 0;
                }
            }

            @media (hover: none) {
                @container ptc-app (width <= 1400px) {
                    .main-menu:hover,
                    .main-menu[data-bubble-hover] {
                        max-width: 4em;
                        box-shadow: none;
                        --menu-bg: hsl(0 0 95%);
                        background-color: var(--menu-bg);
                    }
                }
            }

            @media print {
                :host {
                    position: static;
                }

                .main {
                    padding: 0 !important;
                }

                .main-views {
                    border: none;
                }

                .main,
                .main-views > * {
                    display: block;
                    position: static;
                    max-width: none;
                }
            }
        `,
    ];

    @property({ type: Boolean, reflect: true, attribute: "singleton-container" })
    readonly singletonContainer = true;

    @state()
    private _page: string = "";

    @state()
    private _assistantDragActive = false;

    @state()
    private _assistantOverDock = false;

    @state()
    private _assistantBubbleDocked = isAssistantBubbleInitiallyDocked();

    @state()
    private _assistantBubbleHover = false;

    /** Drives the menu logo's symbol↔full morph: true in expanded states. */
    @state()
    private _menuLogoExpanded = true;

    @query(".main-menu .logo")
    private _menuLogoEl?: Logo;

    /** Mirrors the `ptc-app (width <= 1400px)` container query: the menu is a collapsed rail. */
    private _menuCollapsed = false;

    private _hoverQuery = window.matchMedia("(hover: hover)");

    private _resizeObserver = new ResizeObserver(([entry]) => {
        const collapsed = entry.contentBoxSize[0].inlineSize <= 1400;
        if (collapsed === this._menuCollapsed) return;
        this._menuCollapsed = collapsed;
        this._syncMenuLogoExpanded();
    });

    private _menuLogoCanTrigger = true;
    private _menuLogoCursorOnMenu = false;
    private _menuLogoAnimationPlaying = false;

    @singleton("ptc-update-installed-dialog")
    private _updateInstalledDialog: UpdateInstalledDialog;

    @singleton("ptc-accept-terms-dialog")
    private _acceptTermsDialog: AcceptTermsDialog;

    readonly active = true;

    private _onBubbleDragStart = () => {
        this._assistantDragActive = true;
    };

    private _onBubbleDragEnd = () => {
        this._assistantDragActive = false;
        this._assistantOverDock = false;
    };

    private _onBubbleOverDock = (e: Event) => {
        this._assistantOverDock = (e as CustomEvent<{ over: boolean }>).detail.over;
    };

    private _onAssistantBubbleDockedChanged = (e: Event) => {
        this._assistantBubbleDocked = (e as CustomEvent<{ docked: boolean }>).detail.docked;
    };

    private _onAssistantBubbleHover = (e: Event) => {
        const hovering = (e as CustomEvent<{ hovering: boolean }>).detail.hovering;
        this._assistantBubbleHover = hovering;
        if (hovering) {
            this._onMenuMouseEnter();
        } else {
            this._onMenuMouseLeave();
        }
    };

    private _syncMenuLogoExpanded = () => {
        // Wide: menu is always expanded, so is the logo.
        // Collapsed (hover or touch): default collapsed; the hover handlers re-expand
        // in the auto-collapse case as the pointer enters the menu.
        this._menuLogoExpanded = !this._menuCollapsed;
    };

    private get _menuAutoCollapses() {
        return this._menuCollapsed && this._hoverQuery.matches;
    }

    private _maybeResetMenuLogoTrigger() {
        if (!this._menuLogoCursorOnMenu && !this._menuLogoAnimationPlaying) {
            this._menuLogoCanTrigger = true;
        }
    }

    private _onMenuMouseEnter = () => {
        if (this._menuAutoCollapses) this._menuLogoExpanded = true;
    };

    private _onMenuMouseLeave = () => {
        if (this._menuAutoCollapses) this._menuLogoExpanded = false;
    };

    private _onLogoWrapperMouseEnter = () => {
        this._menuLogoCursorOnMenu = true;
        if (!this._menuLogoCanTrigger || !this._menuLogoEl) return;

        this._menuLogoCanTrigger = false;
        this._menuLogoAnimationPlaying = true;
        void this._menuLogoEl.playOnce().then(() => {
            this._menuLogoAnimationPlaying = false;
            this._maybeResetMenuLogoTrigger();
        });
    };

    private _onLogoWrapperMouseLeave = () => {
        this._menuLogoCursorOnMenu = false;
        this._maybeResetMenuLogoTrigger();
    };

    async connectedCallback() {
        super.connectedCallback();

        this.addEventListener("ptc-ui-chat-bubble-drag-start", this._onBubbleDragStart);
        this.addEventListener("ptc-ui-chat-bubble-drag-end", this._onBubbleDragEnd);
        this.addEventListener("assistant-bubble-over-dock", this._onBubbleOverDock);
        this.addEventListener("assistant-bubble-docked-changed", this._onAssistantBubbleDockedChanged);
        this.addEventListener("assistant-bubble-hover", this._onAssistantBubbleHover);

        this._resizeObserver.observe(this);

        try {
            await app.loaded;
            await this.routeChanged(this.router.path, this.router.params);
        } catch (e) {
            // Reject isReady so embedded hosts get a pentacode-error instead
            // of waiting forever; rethrow to keep the standalone console noise.
            failed(e);
            throw e;
        }
        ready();

        if (app.state.session && app.state.session?.companyId) {
            const termsState = await app.termsNeedToBeAccepted();
            if (termsState) {
                await showAcceptTermsDialog(this._acceptTermsDialog, app, termsState);
            }
        }

        if (app.company) {
            this._reloadFeatures();

            if (this.router.path === "") {
                this.go(firstAccessiblePage(routeAccess()));
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener("ptc-ui-chat-bubble-drag-start", this._onBubbleDragStart);
        this.removeEventListener("ptc-ui-chat-bubble-drag-end", this._onBubbleDragEnd);
        this.removeEventListener("assistant-bubble-over-dock", this._onBubbleOverDock);
        this.removeEventListener("assistant-bubble-docked-changed", this._onAssistantBubbleDockedChanged);
        this.removeEventListener("assistant-bubble-hover", this._onAssistantBubbleHover);

        this._resizeObserver.disconnect();
    }

    private _reloadFeatures() {
        this._features = loadEnabledFeatures(app.company?.isBetaCustomer);

        document.documentElement.dataset.features = Object.entries(this._features)
            .filter(([, value]) => value)
            .map(([feature]) => feature)
            .join(",");

        ensureAssistantLoaded(app.company);
    }

    private get _authenticatedRouteMap() {
        return new Map([
            ["dashboard", () => html`<ptc-dashboard></ptc-dashboard>`],
            ["time", () => html`<ptc-time-logging></ptc-time-logging>`],
            ["employees", () => html`<ptc-employees><slot name="submenu" slot="submenu"></slot></ptc-employees>`],
            [
                "settings",
                () =>
                    html`<ptc-settings @logout=${this._logout}
                        ><slot name="submenu" slot="submenu"></slot
                    ></ptc-settings>`,
            ],
            ["roster", () => html`<ptc-roster></ptc-roster>`],
            ["recruiting", () => html`<ptc-recruiting></ptc-recruiting>`],
            ["issues", () => html`<ptc-issues></ptc-issues>`],
            ["revenues", () => html`<ptc-revenues><slot name="submenu" slot="submenu"></slot></ptc-revenues>`],
            ["planning", () => html`<ptc-planning><slot name="submenu" slot="submenu"></slot></ptc-planning>`],
            ["reports", () => html`<ptc-reports><slot name="submenu" slot="submenu"></slot></ptc-reports>`],
            ["exports", () => html`<ptc-exports><slot name="submenu" slot="submenu"></slot></ptc-exports>`],
            ["feedback", () => html`<ptc-feedback></ptc-feedback>`],
        ]);
    }

    private _renderAuthenticatedRoute() {
        const renderer = this._authenticatedRouteMap.get(this._page);
        return renderer ? renderer() : nothing;
    }

    private _renderNonAuthenticatedRoute() {
        switch (this._page) {
            case "login":
                return html`<ptc-login @login-successful=${() => this._reloadFeatures()}></ptc-login>`;
            case "select-company":
                return html`<ptc-select-company></ptc-select-company>`;
            case "setpwd":
                return html`<ptc-set-password></ptc-set-password>`;
            default:
                return nothing;
        }
    }

    private _renderMenu() {
        const employee =
            (app.company && app.account && app.employees.find((e) => e.accountId === app.account!.id)) || null;

        return html`
            <div
                class="main-menu vertical layout noprint"
                ?data-bubble-hover=${this._assistantBubbleHover}
                @mouseenter=${this._onMenuMouseEnter}
                @mouseleave=${this._onMenuMouseLeave}
            >
                <div
                    class="logo-wrapper"
                    @mouseenter=${this._onLogoWrapperMouseEnter}
                    @mouseleave=${this._onLogoWrapperMouseLeave}
                >
                    <ptc-ui-logo class="logo" variant=${this._menuLogoExpanded ? "full" : "symbol"}></ptc-ui-logo>
                    <ptc-popover class="changelog-popover" trigger="hover" alignment="right-bottom">
                        <ptc-scroller class="changelog-scroller">
                            <div class="changelog-version">Version ${fullVersion}</div>
                            <div class="changelog">${unsafeHTML(changelog)}</div>
                        </ptc-scroller>
                    </ptc-popover>
                    ${app.company?.isBetaCustomer ? html`<div class="tiny pill beta-tag">Beta</div>` : nothing}
                </div>

                <button class="transparent account-button">
                    <ptc-avatar .employee=${employee}></ptc-avatar>
                    <div class="account-info">
                        <div class="account-name ellipsis">
                            ${app.account && (app.account.name || app.account.email)}
                        </div>
                        <div class="account-email ellipsis">${app.company && app.company.name}</div>
                    </div>
                </button>

                <ptc-popover class="popover-menu" alignment="right" hide-on-click>
                    <button @click=${() => this.go("settings/account")} class="transparent stretch">
                        <ptc-ui-icon name="icon.feature.settings"></ptc-ui-icon>
                        Einstellungen
                    </button>
                    <button @click=${this._logout} class="transparent stretch">
                        <ptc-ui-icon name="icon.action.log-out"></ptc-ui-icon>
                        Ausloggen
                    </button>
                    <button
                        @click=${() => this.go("select-company")}
                        class="transparent stretch"
                        ?hidden=${!app.account ||
                        !app.account.profiles ||
                        (!app.account.admin && app.account.profiles.length < 2)}
                    >
                        <ptc-ui-icon name="icon.action.switch-company"></ptc-ui-icon>
                        Unternehmen Wechseln
                    </button>
                </ptc-popover>

                ${menuPages().map((page) => this._renderMenuItem(page))}
                ${app.company?.partnerIntegrations?.length ? html`<div class="double-separator"></div>` : nothing}
                ${app.company?.partnerIntegrations?.includes(PartnerIntegration.HorecaHero)
                    ? html`<a href="https://pentacode.horeca-hero.com/login" target="_blank">
                          <span><ptc-ui-product-logo product="horeca" class="icon"></ptc-ui-product-logo></span>
                          <div class="button-label">Horeca Hero</div>
                          <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                      </a>`
                    : nothing}
                ${app.company?.partnerIntegrations?.includes(PartnerIntegration.Intratool)
                    ? html`<a href="https://messe.intratool.de/" target="_blank">
                          <span><ptc-ui-product-logo product="intratool" class="icon"></ptc-ui-product-logo></span>
                          <div class="button-label">Intratool</div>
                          <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                      </a>`
                    : nothing}
                ${app.company?.partnerIntegrations?.includes(PartnerIntegration.Onboard)
                    ? html`<a href="https://pentacode-demo.onboard.org" target="_blank">
                          <span><ptc-ui-product-logo product="onboard" class="icon"></ptc-ui-product-logo></span>
                          <div class="button-label">Onboard</div>
                          <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                      </a>`
                    : nothing}
                ${app.company?.partnerIntegrations?.includes(PartnerIntegration.Uberblick)
                    ? html`<a href="https://app.uberblick.io/" target="_blank">
                          <span><ptc-ui-product-logo product="uberblick" class="icon"></ptc-ui-product-logo></span>
                          <div class="button-label">Überblick</div>
                          <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                      </a>`
                    : nothing}

                <div class="stretch"></div>

                <div class="appearance-controls">
                    <ptc-ui-icon name="icon.navigation.appearance"></ptc-ui-icon>
                    <div class="button-label">Anzeige</div>
                </div>

                <ptc-popover trigger="click" positioning="fixed" preferAlignment="right">
                    <form
                        @click=${(ev: MouseEvent) => ev.stopPropagation()}
                        class="appearance-controls-form"
                        @submit=${(ev: SubmitEvent) => {
                            ev.preventDefault();
                        }}
                    >
                        <fieldset>
                            <legend>Layout</legend>

                            <label class="layout gap-2" for="app.layout">
                                <ptc-ui-toggle
                                    @ptc-ui-toggle-change=${(ev: CustomEvent) => {
                                        const layout = ev.detail.value as AppLayout;
                                        app.updateSettings({ layout }).catch(console.error);
                                    }}
                                    name="layout"
                                    id="app.layout"
                                    .options=${{ off: "centered", on: "dynamic" }}
                                    value=${app.settings.layout}
                                ></ptc-ui-toggle
                                ><span>Vollbild</span></label
                            >
                        </fieldset>
                        <fieldset>
                            <legend>Farbschema</legend>
                            <ptc-color-scheme-signal-watcher
                                ><ptc-ui-icon name="icon.toggle.color-scheme"></ptc-ui-icon
                            ></ptc-color-scheme-signal-watcher>
                        </fieldset>
                    </form>
                </ptc-popover>
                ${menuPages("footer").map((page) => this._renderMenuItem(page))}

                <button class="help-button" @click=${() => openHelp()}>
                    <ptc-ui-icon name="icon.action.help"></ptc-ui-icon>
                    <div class="button-label">Hilfe</div>
                </button>

                ${app.company?.features.includes(Feature.voiceControlAi)
                    ? html`<div
                          class="assistant-dock"
                          ?data-drag-active=${this._assistantDragActive}
                          ?data-drag-hover=${this._assistantOverDock}
                          ?data-undocked=${!this._assistantBubbleDocked}
                      ></div>`
                    : nothing}
            </div>
        `;
    }

    private _renderMenuItem(page: AppPage) {
        const route = appRoute(page);
        const active = this._page === page;
        return html`
            <button
                ?active=${active}
                @click=${() => this.go(page, route.linkParams?.())}
                ?hidden=${!hasPageAccess(page, routeAccess())}
            >
                ${route.icon ? html`<ptc-ui-icon name=${activeIcon(route.icon, active)}></ptc-ui-icon>` : nothing}
                <div class="button-label" data-testid="${page}-navigation-button">${route.label}</div>
            </button>
        `;
    }

    private _hasPageAccess(page: string) {
        return page === "select-company" || hasPageAccess(page, routeAccess());
    }

    async routeChanged(path: string, { c, company, showChangesSince, ...params }: { [param: string]: string }) {
        const [, page] = /^([^/]*)\/?.*/.exec(path) || ["", ""];
        const loggedIn = !!app.account;

        const companyId = Number(c || company);

        if (!loggedIn) {
            if (!["login", "setpwd", "invite"].includes(page)) {
                this.go("login", { next: path }, true);
                return;
            }
        } else if (!isNaN(companyId) && (!app.state.session?.companyId || companyId !== app.state.session.companyId)) {
            await app.selectCompany(companyId);
            restart();
            return;
        } else if (!app.state.session?.companyId) {
            if (page !== "select-company") {
                this.go("select-company", { next: path });
                return;
            }
        } else if (page === "try-assistant") {
            this.go(firstAccessiblePage(routeAccess()), undefined, true);
            void this._openYnaChat();
            return;
        } else if (!this._hasPageAccess(page)) {
            console.error(`No access to page: ${page}`);
            router.reportDeniedRoute(page);
            this.go(firstAccessiblePage(routeAccess()), undefined, true);
            return;
        }

        // Delete company param from params
        this.router.params = params;

        this._page = page;

        const loadingScreen = document.querySelector(".loading-screen");
        loadingScreen?.setAttribute("aria-busy", "false");

        if (showChangesSince) {
            const versionInfo = getUpdateInfo(showChangesSince);
            if (versionInfo.changes) {
                void this._updateInstalledDialog.show(versionInfo);
            } else {
                // TODO: show a small success toast
            }
        }
    }

    private async _openYnaChat() {
        if (!ensureAssistantLoaded(app.company)) {
            void alert(
                "Yna, Ihre persönliche KI-Assistentin, ist für Ihr Unternehmen nicht freigeschaltet. Bitte wenden Sie sich an unseren Support.",
                {
                    title: "Yna nicht verfügbar",
                }
            );
            return;
        }

        const { openFloatingChat } = await import("../contexts/assistant-context");
        await openFloatingChat();
    }

    private async _logout() {
        const confirmed = await confirm("Sind Sie sicher dass Sie sich ausloggen möchten?", "Ausloggen");
        if (confirmed) {
            await app.logout();
            restart();
        }
    }

    render() {
        this.dataset.layout = app.settings.layout;

        return html`
            ${this.offline
                ? html`
                      <div class="offline" id="offlineIndicator">OFFLINE</div>
                      <ptc-ui-tooltip for="offlineIndicator" placement="bottom">
                          Die Applikation konnte keine Verbindung zum Pentacode Server herstellen. Bitte prüfen Sie Ihre
                          Internetverbindung!
                      </ptc-ui-tooltip>
                  `
                : app.company?.effectiveStatus === CompanyStatus.PaymentOverdue
                  ? html`
                        <div class="offline">
                            <ptc-ui-icon name="icon.status.warning:active"></ptc-ui-icon> Zahlung überfällig! Bitte
                            wenden Sie sich an unseren Support.
                        </div>
                    `
                  : ""}
            ${app.api.session && app.company
                ? html`<div class="main horizontal layout">
                      ${appMode !== "embedded" ? this._renderMenu() : nothing}
                      <div class="main-views stretch">${this._renderAuthenticatedRoute()}</div>
                  </div>`
                : this._page === "login"
                  ? this._renderNonAuthenticatedRoute()
                  : nothing}
            ${["select-company", "setpwd"].includes(this._page) ? this._renderNonAuthenticatedRoute() : nothing}
            ${app.company?.features.includes(Feature.voiceControlAi)
                ? html`<ptc-assistant-chat-floating></ptc-assistant-chat-floating>`
                : nothing}
            <slot></slot>
        `;
    }
}
